import {isToday, addDays, getDay, isAfter, isBefore, parseISO, isSameDay} from 'date-fns'
import {setHMToDate} from '@wix/table-reservations-lib/schedule'
import {
  BusinessSchedule as ExperienceBusinessSchedule,
  DayOfWeek,
  Recurrence,
  Entry,
} from '@wix/ambassador-table-reservations-v1-experience/types'

const DAYS_OF_WEEK = [
  DayOfWeek.SUNDAY, // 0
  DayOfWeek.MONDAY, // 1
  DayOfWeek.TUESDAY, // 2
  DayOfWeek.WEDNESDAY, // 3
  DayOfWeek.THURSDAY, // 4
  DayOfWeek.FRIDAY, // 5
  DayOfWeek.SATURDAY, // 6
] as const

const parseTimeString = (timeString: string): {hours: number; minutes: number} => {
  const [hours, minutes] = timeString.split(':').map(Number)
  return {hours, minutes}
}

const createDateTimeFromTime = (date: Date, timeString: string): Date => {
  const {hours, minutes} = parseTimeString(timeString)
  return setHMToDate(date, hours, minutes)
}

const isDateWithinRange = (
  date: Date,
  startDate?: string | null,
  endDate?: string | null,
): boolean => {
  if (startDate && isBefore(date, parseISO(startDate))) {
    return false
  }
  if (endDate && isAfter(date, parseISO(endDate))) {
    return false
  }
  return true
}

const getNextAvailableSlotFromEntry = (entry: Entry): Date | null => {
  const now = new Date()

  // handle one-time schedule
  if (entry.recurrence === Recurrence.ONE_TIME && entry.oneTimeOptions) {
    const {startDate, startTime} = entry.oneTimeOptions

    if (!startDate || !startTime) {
      return null
    }

    const slotDateTime = createDateTimeFromTime(parseISO(startDate), startTime)

    // return if it's in the future
    return isAfter(slotDateTime, now) ? slotDateTime : null
  }

  if (entry.recurrence === Recurrence.WEEKLY && entry.weeklyOptions) {
    const {startDate, endDate, startDaysAndTimes} = entry.weeklyOptions

    if (!startDaysAndTimes?.length) {
      return null
    }

    const today = new Date()
    const scheduleStartDate = startDate ? parseISO(startDate) : today
    const scheduleEndDate = endDate ? parseISO(endDate) : addDays(today, 365)

    // start checking from today or schedule start date, whichever is later
    const searchStartDate = isAfter(today, scheduleStartDate) ? today : scheduleStartDate

    // don't search beyond the schedule end date
    if (isAfter(searchStartDate, scheduleEndDate)) {
      return null
    }

    // look for the next occurrence of any scheduled day within the next 7 days from search start
    // we only need to check 7 days since weekly schedules repeat every week
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const checkDate = addDays(searchStartDate, dayOffset)

      // skip if beyond schedule end date
      if (isAfter(checkDate, scheduleEndDate)) {
        break
      }

      const dayOfWeek = DAYS_OF_WEEK[getDay(checkDate)]
      const daySlots = startDaysAndTimes.filter((slot) => slot.day === dayOfWeek)

      for (const slot of daySlots) {
        if (!slot.time) {
          continue
        }

        const slotDateTime = createDateTimeFromTime(checkDate, slot.time)

        // skip past slots if checking today
        if (isSameDay(checkDate, now) && !isAfter(slotDateTime, now)) {
          continue
        }

        return slotDateTime
      }
    }
  }

  return null
}

export const getNearestWorkingDateForExperience = (
  experienceBusinessSchedule: ExperienceBusinessSchedule,
): Date | undefined => {
  if (!experienceBusinessSchedule?.entries?.length) {
    return undefined
  }

  let earliestSlot: Date | null = null

  // get the next available slot from each schedule entry
  for (const entry of experienceBusinessSchedule.entries) {
    const nextSlot = getNextAvailableSlotFromEntry(entry)

    if (nextSlot && (!earliestSlot || isBefore(nextSlot, earliestSlot))) {
      earliestSlot = nextSlot
    }
  }

  return earliestSlot || undefined
}

const hasAvailableSlotsOnDate = (entry: Entry, date: Date): boolean => {
  const now = new Date()
  const isCheckingToday = isToday(date)

  if (entry.recurrence === Recurrence.ONE_TIME && entry.oneTimeOptions) {
    const {startDate, startTime} = entry.oneTimeOptions

    if (!startDate || !startTime) {
      return false
    }

    const oneTimeDate = parseISO(startDate)
    if (!isSameDay(date, oneTimeDate)) {
      return false
    }

    const slotDateTime = createDateTimeFromTime(oneTimeDate, startTime)

    // return true if it's in the future (or not checking today)
    return !isCheckingToday || isAfter(slotDateTime, now)
  }

  // handle weekly recurring schedule
  if (entry.recurrence === Recurrence.WEEKLY && entry.weeklyOptions) {
    const {startDate, endDate, startDaysAndTimes} = entry.weeklyOptions

    if (!isDateWithinRange(date, startDate, endDate) || !startDaysAndTimes) {
      return false
    }

    const dayOfWeek = DAYS_OF_WEEK[getDay(date)]
    const daySlots = startDaysAndTimes.filter((slot) => slot.day === dayOfWeek)

    for (const slot of daySlots) {
      if (!slot.time) {
        continue
      }

      const slotDateTime = createDateTimeFromTime(date, slot.time)

      // skip past slots if checking today
      if (isCheckingToday && !isAfter(slotDateTime, now)) {
        continue
      }

      return true
    }
  }

  return false
}

export const filterDateForExperience = ({
  date,
  experienceBusinessSchedule,
}: {
  date: Date
  experienceBusinessSchedule?: ExperienceBusinessSchedule
}): boolean => {
  if (!experienceBusinessSchedule?.entries?.length) {
    return false
  }

  return experienceBusinessSchedule.entries.some((entry) => hasAvailableSlotsOnDate(entry, date))
}
